import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  TextInput,
  Textarea,
  Notification, Autocomplete,
} from '@mantine/core';
import { IconAlertCircle, IconBarcode, IconCheck } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import api from '../api/axiosConfig';
import { printBarcodeLabels } from '../api/printBarcodeLabels';

const initialForm = {
  device_type_id: '',
  brand_name: '',
  model_name: '',
  barcode: '',
  serial_number: '',
  purchase_date: '',
  warranty_expiry: '',
  status: 'müsait',
  condition: 'iyi',
  notes: '',
  assign_employee: false,
  assign_employee_id: '',
  assignment_notes: '',
  print_barcode_after_save: true,
};

export default function AddDeviceModal({ opened, onClose, onSuccess }) {
  const [form, setForm] = useState(initialForm);

  const [deviceTypes, setDeviceTypes] = useState([]);
  const [models, setModels] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [loadingLists, setLoadingLists] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generatingBarcode, setGeneratingBarcode] = useState(false);
  const [error, setError] = useState('');
  
  const [brandSuggestions, setBrandSuggestions] = useState([]);
  const [modelSuggestions, setModelSuggestions] = useState([]);

useEffect(() => {
  if (!opened) return;

  const loadInitialData = async () => {
    setLoadingLists(true);
    try {
      const [deviceTypesRes, employeesRes] = await Promise.all([
        api.get('/it-inventory/device-types'),
        api.get('/it-inventory/employees'),
      ]);

      setDeviceTypes(deviceTypesRes.data || []);
      setEmployees(employeesRes.data || []);
    } catch (err) {
      console.error(err);
      setError('Listeler yüklenirken hata oluştu.');
    } finally {
      setLoadingLists(false);
    }
  };

  loadInitialData();
}, [opened]);

  useEffect(() => {
    if (!opened) return;

    const loadModels = async () => {
      if (!form.device_type_id || !form.brand_id) {
        setModels([]);
        setForm((prev) => ({ ...prev, model_id: '' }));
        return;
      }

      try {
        const res = await api.get('/it-inventory/models', {
          params: {
            device_type_id: Number(form.device_type_id),
            brand_id: Number(form.brand_id),
          },
        });
        setModels(res.data || []);
      } catch (err) {
        console.error(err);
        setModels([]);
      }
    };

    loadModels();
  }, [form.device_type_id, form.brand_id, opened]);

  const resetAndClose = () => {
    setForm(initialForm);
    setError('');
    setModels([]);
    onClose();
  };

const generateBarcode = async () => {
  if (!form.device_type_id || !form.brand_name.trim() || !form.model_name.trim()) {
    setError('Barkod oluşturmak için önce cihaz türü, marka ve model girin.');
    return;
  }

  setGeneratingBarcode(true);
  setError('');

  try {
    const res = await api.post('/it-inventory/generate-barcode', {
      device_type_id: Number(form.device_type_id),
      brand_name: form.brand_name.trim(),
      model_name: form.model_name.trim(),
    });

    setForm((prev) => ({
      ...prev,
      barcode: res.data.barcode,
    }));
  } catch (err) {
    console.error(err);
    setError(err.response?.data?.detail || 'Barkod oluşturulamadı.');
  } finally {
    setGeneratingBarcode(false);
  }
};

  const fetchBrandSuggestions = async (value) => {
  try {
    const res = await api.get('/it-inventory/brands/suggest', {
      params: { query: value || '' },
    });

    setBrandSuggestions((res.data || []).map((item) => item.name));
  } catch (err) {
    console.error(err);
    setBrandSuggestions([]);
  }
};

const fetchModelSuggestions = async (value) => {
  if (!form.device_type_id || !form.brand_name.trim()) {
    setModelSuggestions([]);
    return;
  }

  try {
    const res = await api.get('/it-inventory/models/suggest', {
      params: {
        device_type_id: Number(form.device_type_id),
        brand_name: form.brand_name.trim(),
        query: value || '',
      },
    });

    setModelSuggestions((res.data || []).map((item) => item.name));
  } catch (err) {
    console.error(err);
    setModelSuggestions([]);
  }
};

    const handleSubmit = async () => {
    if (
    !form.device_type_id ||
    !form.brand_name?.trim() ||
    !form.model_name?.trim() ||
    !form.barcode?.trim() ||
    !form.serial_number?.trim()
    ) {
    setError('Cihaz türü, marka, model, barkod ve seri numarası zorunludur.');
    return;
    }

    if (form.assign_employee && !form.assign_employee_id) {
        setError('Personel ataması seçiliyse personel seçimi zorunludur.');
        return;
    }

    setSubmitting(true);
    setError('');

    try {
        const res = await api.post('/it-inventory/devices', {
        barcode: form.barcode.trim(),
        device_type_id: Number(form.device_type_id),
        brand_name: form.brand_name.trim(),
        model_name: form.model_name.trim(),
        serial_number: form.serial_number.trim(),
        purchase_date: form.purchase_date || null,
        warranty_expiry: form.warranty_expiry || null,
        status: form.status,
        condition: form.condition,
        notes: form.notes?.trim() || null,
        assign_employee_id: form.assign_employee ? Number(form.assign_employee_id) : null,
        assignment_notes: form.assign_employee ? (form.assignment_notes?.trim() || null) : null,
        });

        const createdDevice = res.data;

        notifications.show({
        title: 'Başarılı',
        message: 'Yeni cihaz başarıyla eklendi.',
        color: 'teal',
        icon: <IconCheck size={16} />,
        });

        if (form.print_barcode_after_save && createdDevice?.id) {
        try {
            await printBarcodeLabels([createdDevice.id]);
        } catch (printError) {
            console.error(printError);
            notifications.show({
            title: 'Uyarı',
            message: 'Cihaz kaydedildi ancak barkod etiketi yazdırılamadı.',
            color: 'yellow',
            });
        }
        }

        onSuccess?.();
        resetAndClose();
    } catch (err) {
        console.error(err);
        setError(err.response?.data?.detail || 'Cihaz eklenirken hata oluştu.');
    } finally {
        setSubmitting(false);
    }
    };

  const deviceTypeOptions = deviceTypes.map((item) => ({
    value: String(item.id),
    label: item.name,
  }));

  const employeeOptions = employees.map((emp) => ({
    value: String(emp.id),
    label: `${emp.first_name} ${emp.last_name} ${
      emp.employee_type === 'OFFICE'
        ? emp.department ? `- ${emp.department}` : '- Merkez Ofis'
        : emp.site_name ? `- ${emp.site_name}` : '- Şantiye'
    }`,
  }));

  return (
    <Modal
      opened={opened}
      onClose={resetAndClose}
      title="Yeni Cihaz Ekle"
      size="xl"
      centered
      radius="lg"
    >
      <Stack>
        {error && (
          <Notification color="red" icon={<IconAlertCircle size={16} />} onClose={() => setError('')}>
            {error}
          </Notification>
        )}

        {loadingLists ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <>
            <SimpleGrid cols={{ base: 1, md: 3 }}>
                <Select
                label="Cihaz Türü"
                placeholder="Seçin"
                data={deviceTypeOptions}
                value={form.device_type_id}
                onChange={(value) =>
                    setForm((prev) => ({
                    ...prev,
                    device_type_id: value || '',
                    brand_name: '',
                    model_name: '',
                    barcode: '',
                    }))
                }
                searchable
                />

                <Autocomplete
                label="Marka"
                placeholder="Örn: Dell"
                data={brandSuggestions}
                value={form.brand_name}
                onChange={(value) => {
                    setForm((prev) => ({
                    ...prev,
                    brand_name: value,
                    model_name: '',
                    barcode: '',
                    }));
                    fetchBrandSuggestions(value);
                }}
                />

                <Autocomplete
                label="Model"
                placeholder="Örn: Latitude 5420"
                data={modelSuggestions}
                value={form.model_name}
                onChange={(value) => {
                    setForm((prev) => ({
                    ...prev,
                    model_name: value,
                    barcode: '',
                    }));
                    fetchModelSuggestions(value);
                }}
                disabled={!form.device_type_id || !form.brand_name.trim()}
                />

            </SimpleGrid>

            <Group align="end" grow>
              <TextInput
                label="Barkod"
                placeholder="Otomatik üretilecek"
                value={form.barcode}
                onChange={(e) => setForm((prev) => ({ ...prev, barcode: e.target.value }))}
              />
              <Button
                leftSection={<IconBarcode size={16} />}
                onClick={generateBarcode}
                loading={generatingBarcode}
              >
                Barkod Oluştur
              </Button>
            </Group>

            <SimpleGrid cols={{ base: 1, md: 2 }}>
              <TextInput
                label="Seri Numarası"
                value={form.serial_number}
                onChange={(e) => setForm((prev) => ({ ...prev, serial_number: e.target.value }))}
              />

              <Select
                label="Durum"
                data={[
                  { value: 'müsait', label: 'Müsait' },
                  { value: 'zimmetli', label: 'Zimmetli' },
                  { value: 'arıza', label: 'Arızalı' },
                  { value: 'hurda', label: 'Hurda' },
                ]}
                value={form.status}
                onChange={(value) => setForm((prev) => ({ ...prev, status: value || 'müsait' }))}
              />
            </SimpleGrid>

            <SimpleGrid cols={{ base: 1, md: 2 }}>
              <TextInput
                type="date"
                label="Satın Alma Tarihi"
                value={form.purchase_date}
                onChange={(e) => setForm((prev) => ({ ...prev, purchase_date: e.target.value }))}
              />

              <TextInput
                type="date"
                label="Garanti Bitiş Tarihi"
                value={form.warranty_expiry}
                onChange={(e) => setForm((prev) => ({ ...prev, warranty_expiry: e.target.value }))}
              />
            </SimpleGrid>

            <Select
              label="Cihaz Durumu / Kondisyon"
              data={[
                { value: 'iyi', label: 'İyi' },
                { value: 'orta', label: 'Orta' },
                { value: 'kötü', label: 'Kötü' },
              ]}
              value={form.condition}
              onChange={(value) => setForm((prev) => ({ ...prev, condition: value || 'iyi' }))}
            />

            <Textarea
              label="Notlar"
              minRows={3}
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
            />

            <Checkbox
            label="Cihazı personele zimmetle"
            checked={form.assign_employee}
            onChange={(e) => {
                const checked = e.currentTarget.checked;

                setForm((prev) => ({
                ...prev,
                assign_employee: checked,
                assign_employee_id: checked ? prev.assign_employee_id : '',
                assignment_notes: checked ? prev.assignment_notes : '',
                status: checked ? 'zimmetli' : 'müsait',
                }));
            }}
            />

            {form.assign_employee && (
            <>
                <Select
                label="Personel Seçimi"
                placeholder="Kayıtlı personel seçin"
                data={employeeOptions}
                value={form.assign_employee_id}
                onChange={(value) =>
                    setForm((prev) => ({
                    ...prev,
                    assign_employee_id: value || '',
                    }))
                }
                searchable
                />

                <Textarea
                label="Zimmet Notu"
                minRows={2}
                value={form.assignment_notes}
                onChange={(e) => {
                    const value = e.currentTarget.value;
                    setForm((prev) => ({
                    ...prev,
                    assignment_notes: value,
                    }));
                }}
                />
            </>
            )}

            <Checkbox
            label="Kaydettikten sonra barkod etiketi yazdır"
            checked={form.print_barcode_after_save}
            onChange={(e) => {
                const checked = e.currentTarget.checked;

                setForm((prev) => ({
                ...prev,
                print_barcode_after_save: checked,
                }));
            }}
            />

            <Group justify="flex-end">
              <Button variant="default" onClick={resetAndClose}>
                Vazgeç
              </Button>
              <Button onClick={handleSubmit} loading={submitting}>
                Cihazı Kaydet
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}